import { faMinus, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { recomposeColor, rgbToHex } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import VoxelText from '../../../components/voxel/Text/VoxelText';
import { formatNumber } from '../../../logic/otherUtilities/formatting/formatNumber';
import { hslToRgb } from './hslToRgb';

export interface HSLColorPickerProps {
  color: string;
  hslColor: number[];
  setColor: (color: string) => void;
}

export const HSLColorPicker: React.FC<HSLColorPickerProps> = ({ hslColor, setColor, color }) => {
  const [localHsl, setLocalHsl] = useState<(number | undefined)[]>(hslColor);

  // Reset local color if needed
  useEffect(() => {
    if (hslColor.every((value, index) => value === localHsl[index])) return;
    setLocalHsl(hslColor);
  }, [hslColor]);

  useEffect(() => {
    try {
      const rgbColor = hslToRgb(localHsl[0], localHsl[1], localHsl[2]);
      const composedLocalColor = recomposeColor({ type: 'rgb', values: rgbColor as any });
      const hexLocalColor = rgbToHex(composedLocalColor);
      if (hexLocalColor?.length !== 7) return;
      if (hexLocalColor?.toLocaleLowerCase() === color?.toLowerCase()) return;

      // If decomposing color worked that means we can update the color
      setColor(hexLocalColor);
    } catch {
      console.log('crash');
    }
  }, [localHsl]);

  const handleChange = (index: number, delta: number, max?: number, min?: number) => {
    setLocalHsl((prev) =>
      prev.map((prevValue, prevIndex) => {
        if (prevIndex !== index) return prevValue;
        const newValue = (prevValue += delta);
        if (max !== undefined && newValue > max) return max;
        if (min !== undefined && newValue < min) return min;
        return newValue;
      })
    );
  };

  return (
    <div className="flex flex-col gap-2 w-64">
      <VoxelText variant="form-caption">HSB</VoxelText>
      <HslSwitch
        title="Hue"
        handleIncrease={() => handleChange(0, 0.01)}
        handleDecrease={() => handleChange(0, -0.01)}
        value={localHsl[0]}
      />
      <HslSwitch
        title="Saturation"
        handleIncrease={() => handleChange(1, 0.01, 1, 0)}
        handleDecrease={() => handleChange(1, -0.01, 1, 0)}
        value={localHsl[1]}
      />

      <HslSwitch
        title="Brightness"
        handleIncrease={() => handleChange(2, 0.01, 1, 0)}
        handleDecrease={() => handleChange(2, -0.01, 1, 0)}
        value={localHsl[2]}
      />
    </div>
  );
};

interface HslSwitchProps {
  title: string;
  value: number;
  handleIncrease: () => void;
  handleDecrease: () => void;
}

const HslSwitch: React.FC<HslSwitchProps> = ({ title, handleDecrease, handleIncrease, value }) => (
  <div className="flex flex-row items-center select-none">
    <div
      onClick={handleDecrease}
      className={`
        cursor-pointer
        px-2 h-9
        rounded-l
        ring-1 ring-gray-300
        hover:bg-gray-100
        active:bg-gray-200
        transition duration-50
        flex items-center
    `}
    >
      <FontAwesomeIcon icon={faMinus} />
    </div>
    <div
      className={`
        ring-1 ring-gray-300
        flex items-center
        h-9 px-3
        cursor-default
        flex-grow
    `}
    >
      <span className="flex-grow">{title}</span>
      <span className="">{formatNumber(value * 100, 0)} </span>
      <span className="text-gray-400 pl-1"> / 100</span>
    </div>

    <div
      onClick={handleIncrease}
      className={`
        cursor-pointer
        px-2 h-9
        rounded-r
        ring-1 ring-gray-300
        hover:bg-gray-100
        active:bg-gray-200
        transition duration-50
        flex items-center
    `}
    >
      <FontAwesomeIcon icon={faPlus} />
    </div>
  </div>
);

export default HSLColorPicker;
